import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Category, CategoryInput } from '../models/category.model';
import { environment } from '../../environments/environment';

/**
 * Service for managing court categories
 * Handles fetching, creating, and updating category data
 */
@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  // Use inject instead of constructor injection
  private http = inject(HttpClient);
  private CATEGORIES_ENDPOINT = `${environment.apiUrl}/categories`;
  
  // State management
  private categoriesSubject = new BehaviorSubject<Category[]>([]);
  
  /**
   * Observable stream of categories
   * Components can subscribe to this for reactive updates
   */
  categories$ = this.categoriesSubject.asObservable();

  /**
   * Fetch all categories
   * @param organizationId Optional organization ID to filter categories
   * @returns Observable of the categories array
   */
  getCategories(organizationId?: string): Observable<Category[]> {
    let params = new HttpParams();
    if (organizationId) {
      params = params.set('organizationId', organizationId);
    }
    return this.http.get<Category[]>(this.CATEGORIES_ENDPOINT, { params })
      .pipe(
        tap(categories => this.categoriesSubject.next(categories)),
        catchError(this.handleError)
      );
  }

  /**
   * Fetch a single category by ID
   * @param id The category ID
   * @returns Observable of the category
   */
  getCategory(id: string): Observable<Category> {
    return this.http.get<Category>(`${this.CATEGORIES_ENDPOINT}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Create a new category
   * @param data The category data
   * @returns Observable that completes when operation is done
   */
  createCategory(data: CategoryInput): Observable<Category> {
    let cleanedData = {
      ...data,
      // Ensure we don't send fields that shouldn't be updated
      createdAt: undefined,
      updatedAt: undefined,
      id: undefined // Ensure ID is not sent for creation
    };

    return this.http.post<Category>(this.CATEGORIES_ENDPOINT, cleanedData, this.httpOptions)
      .pipe(
        tap(createdCategory => {
          // Update local state
          const categories = this.categoriesSubject.value;
          this.categoriesSubject.next([...categories, createdCategory]);
        }),
        catchError(this.handleError)
      )
  }

  /**
   * Update an existing category
   * @param id The ID of the category to update
   * @param data Category data to update
   * @returns Observable that completes when operation is done
   */
  updateCategory(id: string, data: Partial<CategoryInput>): Observable<Category> {
    // Make sure we're only sending the allowed fields
    let cleanedData = {
      ...data,
      // Ensure we don't send fields that shouldn't be updated
      createdAt: undefined,
      updatedAt: undefined,
      id: undefined
    };
    console.log('Sending to server:', cleanedData);

    return this.http.patch<Category>(`${this.CATEGORIES_ENDPOINT}/${id}`, cleanedData, this.httpOptions)
      .pipe(
        tap(updatedCategory => {
          // Update local cache
          const categories = this.categoriesSubject.value;
          const index = categories.findIndex(cat => cat.id === id);
          if (index !== -1) {
            const updatedCategories = [...categories];
            updatedCategories[index] = updatedCategory;
            this.categoriesSubject.next(updatedCategories);
          }
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Refresh the categories in the BehaviorSubject
   * Called internally after mutations to keep state in sync
   */
  refreshCategories(): void {
    this.getCategories().subscribe();
  }
  
  /**
   * Error handler for HTTP requests
   * @param error The HTTP error response
   * @returns An observable that errors
   */
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ein unbekannter Fehler ist aufgetreten';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Fehler: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Fehlercode: ${error.status}\nNachricht: ${error.message}`;
    }
    
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  // Add this property to your service class
  private get httpOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }),
      withCredentials: true
    };
  }
}